/** Round 6: the story Share menu's live line for "Personalized distribution" (a way to the runs from the story). */
import { useMemo } from "react";
import { runCategoryOf, runStateOf, toDistributionShareNote } from "../services/distributions/distributionRunMappers";
import type { DistributionSummaryApi } from "../services/distributions/distributions.types";
import { useDistributions } from "./useDistributions";

/** "attention" when any distribution's last run needs attention (D14: the warning colour); otherwise "default". */
export type DistributionShareNoteTone = "default" | "attention";

export interface DistributionShareNoteState {
  note: string | null;
  tone: DistributionShareNoteTone;
}

export const toDistributionShareNoteTone = (summaries: DistributionSummaryApi[]): DistributionShareNoteTone =>
  summaries.some((summary) => summary.last_run && runCategoryOf(runStateOf(summary.last_run).key) === "attn") ? "attention" : "default";

/** The note text plus its tone. Header.tsx adopts this when the Share menu takes a tone (see redesign-w2-share.md). */
export const useDistributionShareNoteState = (storyId: string | undefined, enabled: boolean): DistributionShareNoteState => {
  const { distributions } = useDistributions(enabled ? storyId : undefined);
  const note = toDistributionShareNote(distributions);
  const tone = toDistributionShareNoteTone(distributions);
  // Primitive deps keep the object stable across renders, so Header's memo isn't invalidated each time.
  return useMemo(() => ({ note, tone }), [note, tone]);
};

/** The note split at its " · " joints; the attention count, always last, is flagged amber when the tone says so. */
export const toDistributionShareNoteSegments = (
  state: DistributionShareNoteState,
): { text: string; tone: "default" | "attention" }[] | undefined => {
  if (!state.note) return undefined;
  const parts = state.note.split(" · ");
  return parts.map((text, index) => ({
    text,
    tone: state.tone === "attention" && index === parts.length - 1 && parts.length > 1 ? "attention" : "default",
  }));
};

/** Text only (unchanged signature, so Header.tsx keeps working). */
export const useDistributionShareNote = (storyId: string | undefined, enabled: boolean): string | null =>
  useDistributionShareNoteState(storyId, enabled).note;
